import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { verifyGoogleState } from '@/lib/google-state';
import { LOGIN_WITH_GOOGLE } from '@/graphql/mutations';

export function GoogleCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation('auth');
    const [loginWithGoogle] = useMutation(LOGIN_WITH_GOOGLE);
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) return;
        calledRef.current = true;

        const error = searchParams.get('error');
        if (error) {
            navigate('/login', { state: { googleError: t('google.error.denied') }, replace: true });
            return;
        }

        // CSRF protection: verify state parameter
        const state = searchParams.get('state');
        if (!verifyGoogleState(state)) {
            navigate('/login', { state: { googleError: t('google.error.invalidState') }, replace: true });
            return;
        }

        const code = searchParams.get('code');
        if (!code) {
            navigate('/login', { replace: true });
            return;
        }

        loginWithGoogle({ variables: { code } })
            .then(async ({ data }) => {
                if (data?.loginWithGoogle?.token) {
                    await login(data.loginWithGoogle.token);
                    navigate('/', { replace: true });
                }
            })
            .catch(() => {
                navigate('/login', { state: { googleError: t('google.error.failed') }, replace: true });
            });
    }, [searchParams, loginWithGoogle, login, navigate, t]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
            <Loader2 className="animate-spin text-emerald-600" size={40} />
            <p className="text-slate-500 font-medium">{t('google.connecting')}</p>
        </div>
    );
}
